/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { formatVndIntegerValue } from "utils";
import DataTable from "./DataTable";
import ActionCell from "./components/ActionCell";
import { GeneralLedgerVouchersService } from "services/general-ledger-vouchers";

const now = new Date();
const defaultYear = String(now.getFullYear());
const defaultMonth = String(now.getMonth() + 1).padStart(2, "0");

const tableData = {
  columns: [
    { Header: "Ngày ghi sổ", accessor: "postingDate" },
    { Header: "Số CTGS", accessor: "voucherNumber" },
    { Header: "Diễn giải", accessor: "description" },
    { Header: "TK đối ứng", accessor: "correspondingAccount", align: "center" },
    { Header: "Nợ", accessor: "debitAmount", align: "right" },
    { Header: "Có", accessor: "creditAmount", align: "right" },
    { Header: "Actions", accessor: "action", sorted: false, align: "center" },
  ],
  rows: [],
};

const buildRows = (items, filters, baseRoute) =>
  (items || []).map((item) => {
    const params = new URLSearchParams({
      year: String(filters.year || defaultYear),
      month: String(filters.month || defaultMonth),
      accountCode: String(filters.accountCode || ""),
      searchBy: String(filters.searchBy || ""),
    }).toString();

    return {
      id: item.id,
      postingDate: item.postingDate,
      voucherNumber: item.voucherNumber,
      description: item.description,
      correspondingAccount: item.correspondingAccount,
      debitAmount: formatVndIntegerValue(item.debitAmount || 0),
      creditAmount: formatVndIntegerValue(item.creditAmount || 0),
      action: (
        <ActionCell
          item={{
            viewLink: `${baseRoute}/view?${params}`,
          }}
        />
      ),
    };
  });

export default function GeneralLedger({ baseRoute = "/templates/general-ledger" }) {
  const [loading, setLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [dataTable, setDataTable] = useState(tableData);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({
    year: defaultYear,
    month: defaultMonth,
    accountCode: "",
    searchBy: "",
  });
  const [query, setQuery] = useState({
    year: defaultYear,
    month: defaultMonth,
    accountCode: "",
    searchBy: "",
    sortBy: "voucherDate",
    sortType: "asc",
  });
  const [paginationData, setPaginationData] = useState({ page: 1, count: 0, size: 10 });

  useEffect(() => {
    fetchAccounts(filters.year);
  }, [filters.year, filters.accountCode]);

  useEffect(() => {
    if (!query.accountCode) {
      setDataTable((prev) => ({ ...prev, rows: [] }));
      setPaginationData((prev) => ({ ...prev, count: 0 }));
      return;
    }

    fetchData();
  }, [query, paginationData.page]);

  const fetchAccounts = async (year) => {
    try {
      setAccountsLoading(true);
      const response = await GeneralLedgerVouchersService.getAccounts({ year });
      const payload = response?.data || response || {};
      const items = payload.items || [];
      setAccounts(items);

      // Auto-select first account if none selected
      if (items.length > 0 && !filters.accountCode) {
        const firstAccount = items[0].accountCode;
        setFilters((prev) => ({ ...prev, accountCode: firstAccount }));
        setQuery((prev) => ({ ...prev, accountCode: firstAccount }));
      }
    } catch (error) {
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await GeneralLedgerVouchersService.getPaginate({
        ...query,
        page: paginationData.page,
        limit: paginationData.size,
      });
      const payload = response?.data || response || {};

      setPaginationData((prev) => ({
        ...prev,
        count: payload.paginate?.count || 0,
        size: payload.paginate?.size || 10,
      }));

      setDataTable((prev) => ({
        ...prev,
        rows: buildRows(payload.items || [], query, baseRoute),
      }));
    } catch (error) {
      setDataTable((prev) => ({ ...prev, rows: [] }));
      setPaginationData((prev) => ({ ...prev, count: 0 }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card style={{ height: "100%" }}>
          <DataTable
            table={dataTable}
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            accounts={accounts}
            accountsLoading={accountsLoading}
            query={query}
            setQuery={setQuery}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}
